import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { requestPasswordReset } from '../../../services/authService';

/**
 * Modal para solicitar reset de senha via email.
 * Permite usuários autenticados ou não solicitarem reset de senha.
 * Se o usuário estiver logado, o email da sessão ativa é preenchido automaticamente.
 * Envia requisição POST para /api/v1/users/password/reset com payload:
 * { email, message, link } onde link é obtido de EMAIL_SERVER_RESET_PASSWORD (.env).
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
function ResetPasswordModal({ open, onClose }) {
  // Obter email do usuário logado do localStorage
  const getLoggedUserEmail = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.email || '';
    } catch {
      return '';
    }
  };

  const [email, setEmail] = useState(getLoggedUserEmail());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Atualizar email quando o modal abrir (caso usuário tenha feito login/logout)
  useEffect(() => {
    if (open) {
      setEmail(getLoggedUserEmail());
    }
  }, [open]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email é obrigatório');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Email inválido');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await requestPasswordReset(email);
      setSuccess(true);
      setEmail('');
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 3000);
    } catch (err) {
      setError(err.message || 'Erro ao solicitar redefinição de senha');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setEmail(getLoggedUserEmail());
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockResetOutlinedIcon />
          Redefinir Senha
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={isLoading}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Email de redefinição de senha enviado com sucesso! Verifique sua caixa de entrada.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Digite seu email para receber as instruções de redefinição de senha.
            </Typography>
            
            {error && (
              <Alert 
                severity={error.includes('requisição válida') ? 'info' : 'error'} 
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                disabled={isLoading}
                autoFocus
                required
              />
            </Box>
          </>
        )}
      </DialogContent>
      
      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Enviando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default ResetPasswordModal;
